<template>
  <div
    :class="['tree-row tree-document-row', { active: activeDocumentId === document.id, 'has-row-actions': canEdit }]"
    :data-sidebar-document-id="document.id"
    :draggable="!editing"
    @dragstart.stop="dragStart"
  >
    <template v-if="editing">
      <FileText class="icon tree-icon" />
      <input
        ref="renameInput"
        v-model="draft"
        class="tree-rename-input"
        @click.stop
        @keydown.enter.prevent="commitRename"
        @keydown.esc.prevent="cancelRename"
        @blur="commitRename"
      />
    </template>
    <template v-else>
      <button
        type="button"
        class="tree-label"
        @click="$emit('open', document.id)"
        @dblclick.stop.prevent="beginRename"
      >
        <FileText class="icon tree-icon" />
        <span class="tree-name">{{ document.title }}</span>
        <DocumentPresenceBadge :participants="workspace.getDocumentPresence(document.id)" />
      </button>
      <div v-if="canEdit" class="tree-row-actions">
        <div class="tree-actions">
          <button type="button" class="icon-button" title="Delete document" @click.stop="$emit('delete', document)">
            <Trash2 class="icon" />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { FileText, Trash2 } from 'lucide-vue-next';
import { nextTick, ref } from 'vue';
import type { EntityId } from '../api/entityId';
import { setDraggedDocument } from '../dragDrop';
import { useWorkspaceStore, type WorkspaceDocumentTree } from '../stores/workspace';
import DocumentPresenceBadge from './DocumentPresenceBadge.vue';

const props = defineProps<{
  document: WorkspaceDocumentTree;
  canEdit: boolean;
  activeDocumentId: EntityId | null;
}>();

const emit = defineEmits<{
  open: [documentId: EntityId];
  rename: [documentId: EntityId, title: string];
  delete: [document: WorkspaceDocumentTree];
  dragStart: [document: WorkspaceDocumentTree, canMove: boolean];
}>();

const workspace = useWorkspaceStore();
const editing = ref(false);
const draft = ref('');
const renameInput = ref<HTMLInputElement | null>(null);

function dragStart(event: DragEvent): void {
  setDraggedDocument(event, props.document.id);
  emit('dragStart', props.document, props.canEdit);
}

async function beginRename(): Promise<void> {
  if (!props.canEdit) {
    return;
  }

  draft.value = props.document.title;
  editing.value = true;
  await nextTick();
  renameInput.value?.focus();
  renameInput.value?.select();
}

function commitRename(): void {
  if (!editing.value) {
    return;
  }

  editing.value = false;
  const title = draft.value.trim();
  if (title && title !== props.document.title) {
    emit('rename', props.document.id, title);
  }
}

function cancelRename(): void {
  editing.value = false;
}
</script>
